import React, { useMemo } from "react";
import {
  EuiFieldSearch,
  EuiFilterButton,
  EuiFilterGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSelect,
  EuiText,
} from "@elastic/eui";
import { OpsanioApplianceData } from ".";
import { OpsanioWaves } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { HealthStatus } from "./health";
import { FleetHealthEntry } from "./useFleetHealth";

export type ArchiveFilter = "active" | "archived" | "all";
/** "attention" = critical OR warning (the page's "needs attention" figure). */
export type StatusFilter = HealthStatus | "all" | "attention";

export interface ApplianceFilterState {
  /** Free-text query across name, VPC, project, owner and location. */
  search: string;
  wave: OpsanioWaves | "all";
  /** Live health status; "unknown" means unmonitored. */
  status: StatusFilter;
  sales: string;
  technical: string;
  archive: ArchiveFilter;
}

export const defaultApplianceFilters: ApplianceFilterState = {
  search: "",
  wave: "all",
  status: "all",
  sales: "all",
  technical: "all",
  archive: "active",
};

/** Haystack for the free-text filter - every field the search claims to cover. */
const searchHaystack = (a: OpsanioApplianceData): string =>
  [
    a.displayName,
    a.common?.metadata?.name,
    (a.common?.metadata as any)?.project,
    (a.common?.metadata as any)?.owner,
    a.common?.location?.city,
    a.common?.location?.country,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/**
 * Apply the shared appliance filters (search / wave / status / sales /
 * technical / archive).
 *
 * Lifted out of the table so the page can drive BOTH the health overview charts
 * and the table from one filter state - the graphs react exactly like the table.
 *
 * The status filter needs live health, which arrives asynchronously: while the
 * sweep is still running an appliance has no entry yet and counts as `unknown`
 * (unmonitored), never as healthy.
 */
export const filterAppliances = (
  appliances: OpsanioApplianceData[],
  f: ApplianceFilterState,
  healthById: Record<string, FleetHealthEntry> = {},
): OpsanioApplianceData[] =>
  appliances.filter((a) => {
    if (f.wave !== "all" && a.wave !== f.wave) return false;

    const query = f.search.trim().toLowerCase();
    if (query !== "" && !searchHaystack(a).includes(query)) return false;

    if (f.status !== "all") {
      const status = healthById[a.id]?.health.status ?? "unknown";
      if (f.status === "attention") {
        if (status !== "critical" && status !== "warning") return false;
      } else if (status !== f.status) return false;
    }

    const contacts = a.common?.general?.contacts;
    if (f.sales !== "all" && !contacts?.sales?.includes(f.sales)) return false;
    if (f.technical !== "all" && !contacts?.technical?.includes(f.technical))
      return false;

    if (f.archive === "active" && a.archived) return false;
    if (f.archive === "archived" && !a.archived) return false;

    return true;
  });

/**
 * Appliances per live status under every filter EXCEPT the status filter
 * itself, so each status chip shows how many rows selecting it would give.
 */
export const statusCounts = (
  appliances: OpsanioApplianceData[],
  f: ApplianceFilterState,
  healthById: Record<string, FleetHealthEntry> = {},
): Record<HealthStatus | "all", number> => {
  const base = filterAppliances(appliances, { ...f, status: "all" }, healthById);
  const counts: Record<HealthStatus | "all", number> = {
    all: base.length,
    critical: 0,
    warning: 0,
    healthy: 0,
    unknown: 0,
  };
  for (const a of base) {
    counts[healthById[a.id]?.health.status ?? "unknown"] += 1;
  }
  return counts;
};

export const ApplianceFilters: React.FC<{
  /** Full fleet - used to build the sales/technical option lists. */
  appliances: OpsanioApplianceData[];
  filters: ApplianceFilterState;
  onChange: (next: ApplianceFilterState) => void;
  shown: number;
  total: number;
  /** Per-status counts for the status chips (see statusCounts). */
  counts: Record<HealthStatus | "all", number>;
  /** Rendered at the end of the row (the table's column-set switch). */
  extra?: React.ReactNode;
}> = ({ appliances, filters, onChange, shown, total, counts, extra }) => {
  const { translate } = useAnketaI18n();

  const waveOptions: { value: OpsanioWaves | "all"; text: string }[] = [
    {
      value: "all",
      text: translate("anketa.opsanio.table.filter.wave.all", {
        defaultMessage: "All Waves",
      }),
    },
    { value: "dev", text: "Dev" },
    { value: "main", text: "Main" },
    { value: "qa", text: "QA" },
    { value: "wave1", text: "Wave 1" },
    { value: "wave2", text: "Wave 2" },
    { value: "wave3", text: "Wave 3" },
    { value: "debug", text: "Debug" },
  ];

  const archiveOptions: { value: ArchiveFilter; text: string }[] = [
    {
      value: "active",
      text: translate("anketa.opsanio.table.filter.archive.active", {
        defaultMessage: "Active only",
      }),
    },
    {
      value: "archived",
      text: translate("anketa.opsanio.table.filter.archive.archived", {
        defaultMessage: "Archived only",
      }),
    },
    {
      value: "all",
      text: translate("anketa.opsanio.table.filter.archive.all", {
        defaultMessage: "All",
      }),
    },
  ];

  const salesOptions = useMemo(() => {
    const names = [
      ...new Set(
        appliances.flatMap((a) => a.common?.general?.contacts?.sales ?? []),
      ),
    ].filter(Boolean);
    return [
      {
        value: "all",
        text: translate("anketa.opsanio.table.filter.sales.all", {
          defaultMessage: "All Sales",
        }),
      },
      ...names.map((n) => ({ value: n, text: n })),
    ];
  }, [appliances, translate]);

  const technicalOptions = useMemo(() => {
    const names = [
      ...new Set(
        appliances.flatMap((a) => a.common?.general?.contacts?.technical ?? []),
      ),
    ].filter(Boolean);
    return [
      {
        value: "all",
        text: translate("anketa.opsanio.table.filter.technical.all", {
          defaultMessage: "All Technical",
        }),
      },
      ...names.map((n) => ({ value: n, text: n })),
    ];
  }, [appliances, translate]);

  // Status is the most used filter, so it is one click: a chip per status
  // with its count, instead of a select.
  const statusChips: Array<{ value: StatusFilter; label: string; count: number }> = [
    {
      value: "all",
      label: translate("anketa.opsanio.table.filter.status.chip.all", {
        defaultMessage: "All",
      }),
      count: counts.all,
    },
    {
      value: "critical",
      label: translate("anketa.opsanio.overview.status.critical", {
        defaultMessage: "Critical",
      }),
      count: counts.critical,
    },
    {
      value: "warning",
      label: translate("anketa.opsanio.overview.status.warning", {
        defaultMessage: "Warning",
      }),
      count: counts.warning,
    },
    {
      value: "healthy",
      label: translate("anketa.opsanio.overview.status.healthy", {
        defaultMessage: "Healthy",
      }),
      count: counts.healthy,
    },
    {
      value: "unknown",
      label: translate("anketa.opsanio.overview.status.unmonitored", {
        defaultMessage: "Unmonitored",
      }),
      count: counts.unknown,
    },
  ];

  return (
    <EuiFlexGroup gutterSize="s" alignItems="center" wrap responsive={false}>
      <EuiFlexItem grow={false} style={{ width: 300 }}>
        <EuiFieldSearch
          fullWidth
          compressed
          incremental
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder={translate("anketa.opsanio.table.filter.search.placeholder", {
            defaultMessage: "Search appliance, VPC, project or location…",
          })}
          aria-label={translate("anketa.opsanio.table.filter.search.aria", {
            defaultMessage: "Search the fleet",
          })}
        />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiFilterGroup
          aria-label={translate("anketa.opsanio.table.filter.status.aria", {
            defaultMessage: "Filter by live health status",
          })}
        >
          {statusChips.map((chip) => {
            // "attention" (from the KPI card) highlights both of its parts.
            const active =
              filters.status === chip.value ||
              (filters.status === "attention" &&
                (chip.value === "critical" || chip.value === "warning"));
            return (
              <EuiFilterButton
                key={chip.value}
                hasActiveFilters={active}
                isSelected={active}
                numFilters={chip.count}
                onClick={() =>
                  onChange({
                    ...filters,
                    status:
                      filters.status === chip.value && chip.value !== "all"
                        ? "all"
                        : chip.value,
                  })
                }
              >
                {chip.label}
              </EuiFilterButton>
            );
          })}
        </EuiFilterGroup>
      </EuiFlexItem>
      <EuiFlexItem grow={true} />
      <EuiFlexItem grow={false} style={{ minWidth: 140 }}>
        <EuiSelect
          compressed
          options={waveOptions}
          value={filters.wave}
          onChange={(e) =>
            onChange({ ...filters, wave: e.target.value as OpsanioWaves | "all" })
          }
          aria-label={translate("anketa.opsanio.table.filter.wave.aria", {
            defaultMessage: "Filter by wave",
          })}
        />
      </EuiFlexItem>
      <EuiFlexItem grow={false} style={{ minWidth: 150 }}>
        <EuiSelect
          compressed
          options={salesOptions}
          value={filters.sales}
          onChange={(e) => onChange({ ...filters, sales: e.target.value })}
          aria-label={translate("anketa.opsanio.table.filter.sales.aria", {
            defaultMessage: "Filter by sales contact",
          })}
        />
      </EuiFlexItem>
      <EuiFlexItem grow={false} style={{ minWidth: 150 }}>
        <EuiSelect
          compressed
          options={technicalOptions}
          value={filters.technical}
          onChange={(e) => onChange({ ...filters, technical: e.target.value })}
          aria-label={translate("anketa.opsanio.table.filter.technical.aria", {
            defaultMessage: "Filter by technical contact",
          })}
        />
      </EuiFlexItem>
      <EuiFlexItem grow={false} style={{ minWidth: 140 }}>
        <EuiSelect
          compressed
          options={archiveOptions}
          value={filters.archive}
          onChange={(e) =>
            onChange({ ...filters, archive: e.target.value as ArchiveFilter })
          }
          aria-label={translate("anketa.opsanio.table.filter.archive.aria", {
            defaultMessage: "Filter by archive state",
          })}
        />
      </EuiFlexItem>
      {extra && <EuiFlexItem grow={false}>{extra}</EuiFlexItem>}
      <EuiFlexItem grow={false}>
        <EuiText size="xs" color="subdued">
          {translate("anketa.opsanio.table.count.summary", {
            values: { shown, total },
            defaultMessage: "{{shown}} of {{total}} appliances",
          })}
        </EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
